/**
 * Why BetOptimized (/why) — the marketing page, in Floodlight (v1.71): the
 * original black ground with its blue glow, glass panels and the purple/blue
 * accents, set in Floodlight type. Whatever the pointer touches lifts or
 * brightens (components/floodlight/floodlightCss.js); all motion is off under
 * reduced motion.
 *
 * Copy rule: real features only. No invented stats, results or testimonials,
 * and no claims about live EV results (odds are on a sandbox feed for now).
 */
import React from 'react';
import { View, Text, Pressable, StyleSheet, Platform, useWindowDimensions } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation } from '../context/NavigationContext';
import { useAuth } from '../context/AuthContext';
import Reveal from '../components/motion/Reveal';
import { FLPage, FLButton, FLHeadline, FLMarquee, FLKicker, FLRule, FL, FONT, display, label, body, gutter, glass, ACCENT_GRADIENT } from '../components/floodlight';

// Recognizable names from utils/sportsbooks.json (the books we price).
const BOOKS = ['DraftKings', 'FanDuel', 'BetMGM', 'Caesars', 'Fanatics', 'BetRivers', 'Hard Rock Bet', 'Pinnacle', 'Circa Sports', 'Bovada', 'BetOnline', 'Novig', 'Kalshi', 'Polymarket', 'PrizePicks', 'Underdog Fantasy', 'Fliff', 'Betfair'];

const STEPS = [
    ['Ask it plainly', 'Type the bet the way you think it: "lebron over 25.5 points", "chiefs -3.5", "nba player points". No menus to dig through.'],
    ['See every book', 'Every price for that line, side by side, devigged against the whole market so you see the fair odds, not one book\'s opinion.'],
    ['Take the best number', 'Ranked by expected value, with the book that pays the most and how many books carry it. Then bet it where you already play.'],
];

const FEATURES = [
    ['Natural-language search', 'Players, teams, markets and lines in one box, across sports and leagues.'],
    ['Same-game parlay optimizer', 'Stack legs from one game and price the combination at the major books.'],
    ['Fair odds on every price', 'Devigged against the full market, not a single sportsbook.'],
    ['Strictness you control', 'Exact matches only, or broaden to related markets and nearby lines with one slider.'],
    ['Background searches', 'Queue searches and keep browsing; results land when they are ready.'],
    ['Community', 'Share bets, compare reads and talk lines with other members.'],
];

export default function WhyScreen() {
    const { navigateTo } = useNavigation();
    const { user } = useAuth();
    const { width } = useWindowDimensions();
    const pad = gutter(width);
    const wide = width >= 960;
    // Fit "EVERY MARKET." (the longest line, ~9em in Floodlight caps at width 104) to its column, with headroom.
    const heroCol = wide ? ((Math.min(width, 1480) - pad * 2 - 56) * 1.6) / 2.6 : width - pad * 2;
    const heroSize = Math.max(34, Math.min(96, Math.floor(heroCol / 9.6)));

    const primary = user
        ? { label: 'Open the Optimizer', to: 'SameGameParlay' }
        : { label: 'Start free', to: 'Register' };
    const jumps = [
        ['Search any line', 'Home'],
        ['Open the Optimizer', 'SameGameParlay'],
        ['Compare plans', 'Plans'],
        ['Join the community', 'Community'],
    ];

    return (
        <FLPage bottom={0}>
            {/* Hero: the headline owns the left; quick routes stack on the right. */}
            <View style={[styles.hero, { paddingHorizontal: pad }, wide && styles.heroWide]}>
                <View style={{ flex: wide ? 1.6 : undefined }}>
                    <FLKicker index={1}>BetOptimized</FLKicker>
                    <FLHeadline
                        lines={['Every book.', 'Every market.', 'One edge.']}
                        accentLine={2}
                        size={heroSize}
                        style={{ marginTop: 22 }}
                    />
                    <Text style={[body(width >= 600 ? 19 : 17), styles.lede]}>
                        BetOptimized prices every bet against the whole market. Search any line in plain English, see every sportsbook side by side with the fair odds, and take the best number.
                    </Text>
                    <View style={styles.ctaRow}>
                        <FLButton label={primary.label} variant="green" onPress={() => navigateTo(primary.to)} size="lg" style={styles.cta} />
                        <FLButton label="See plans" variant="outline" onPress={() => navigateTo('Plans')} size="lg" style={styles.cta} />
                    </View>
                </View>
                <View style={[styles.jumps, wide ? { flex: 1, marginTop: 64, alignSelf: 'flex-start' } : { marginTop: 48 }]}>
                    <Text style={[label(11), styles.jumpsHead]}>Go straight to</Text>
                    {jumps.map(([t, to], i) => (
                        <JumpRow key={to} index={i + 1} title={t} last={i === jumps.length - 1} onPress={() => navigateTo(to)} />
                    ))}
                </View>
            </View>

            {/* Books band */}
            <View style={{ marginTop: wide ? 56 : 40 }}>
                <FLMarquee items={BOOKS} size={wide ? 40 : 26} duration={60} />
                <Text style={[body(14), styles.bandNote, { paddingHorizontal: pad }]}>
                    Priced side by side: major US books, sharp offshore books, exchanges, prediction markets and DFS apps, including {BOOKS.slice(0, 4).join(', ')} and Pinnacle.
                </Text>
            </View>

            {/* How it works */}
            <View style={[styles.section, { paddingHorizontal: pad }]}>
                <Reveal>
                    <FLKicker index={2}>How it works</FLKicker>
                    <FLHeadline lines={['Three steps', 'to the number']} size={wide ? 52 : 34} level={2} enter={false} style={{ marginTop: 18 }} />
                </Reveal>
                <View style={[styles.steps, !wide && { flexDirection: 'column' }]}>
                    {STEPS.map(([t, b], i) => (
                        <Reveal key={t} index={i} style={wide && { flex: 1 }}>
                            <View style={[styles.step, !wide && styles.stepStacked]} dataSet={{ flCard: '' }}>
                                <View style={styles.stepBar} dataSet={{ flCardBar: '' }} />
                                <Text style={[display(wide ? 64 : 48, { width: 110, lineHeight: 1 }), styles.stepNum]} aria-hidden>{String(i + 1).padStart(2, '0')}</Text>
                                <Text style={styles.stepTitle} accessibilityRole="header" aria-level={3}>{t}</Text>
                                <Text style={body(15)}>{b}</Text>
                            </View>
                        </Reveal>
                    ))}
                </View>
            </View>

            {/* Statement */}
            <Reveal style={[styles.section, { paddingHorizontal: pad }]}>
                <View style={[styles.statementBox, { paddingHorizontal: wide ? 56 : 24 }]}>
                    <Text style={[styles.statement, { fontSize: wide ? 44 : 26, lineHeight: wide ? 52 : 32 }]}>
                        The market already knows the fair price.{' '}
                        <Text style={styles.highlight}>We show you where a book disagrees.</Text>
                    </Text>
                </View>
            </Reveal>

            {/* Features: a numbered table, each row brightens under the pointer */}
            <View style={[styles.section, { paddingHorizontal: pad }]}>
                <Reveal>
                    <FLKicker index={3}>What you get</FLKicker>
                    <FLHeadline lines={['Built for', 'the number']} size={wide ? 52 : 34} level={2} enter={false} style={{ marginTop: 18 }} />
                </Reveal>
                <View style={{ marginTop: 32 }} accessibilityRole="list">
                    <FLRule />
                    {FEATURES.map(([t, b], i) => (
                        <Reveal key={t} index={i} distance={16}>
                            <View style={[styles.featRow, !wide && styles.featRowStack]} dataSet={{ flRow: 'static' }} accessibilityRole="listitem">
                                <Text style={styles.featNum} dataSet={{ flRowNum: '' }} aria-hidden>{String(i + 1).padStart(2, '0')}</Text>
                                <View style={[styles.featBody, wide && { flexDirection: 'row', alignItems: 'baseline', gap: 24 }]} dataSet={{ flRowBody: '' }}>
                                    <Text style={[styles.featTitle, wide && { width: '42%' }]}>{t}</Text>
                                    <Text style={[body(15), { flex: 1 }]} dataSet={{ flLit: '' }}>{b}</Text>
                                </View>
                                <View dataSet={{ flRowArrow: '' }} aria-hidden>
                                    <Ionicons name="arrow-forward" size={20} color={FL.greenInk} />
                                </View>
                            </View>
                        </Reveal>
                    ))}
                </View>
            </View>

            {/* Closer: a glass stage with a purple bloom */}
            <View style={[styles.closer, { paddingHorizontal: pad, marginTop: wide ? 120 : 80 }]}>
                <Reveal style={[styles.closerInner, { paddingHorizontal: wide ? 56 : 24 }]}>
                    <FLKicker index={4} tone="paper">Start</FLKicker>
                    <FLHeadline lines={['Find', 'your edge.']} accentLine={1} tone="paper" size={wide ? 84 : 44} level={2} enter={false} style={{ marginTop: 22 }} />
                    <Text style={[body(18), styles.closerBody]}>
                        {user ? 'Your account is ready. Run your first search in the Optimizer.' : 'Start on the free plan. No card needed, upgrade when you want more searches.'}
                    </Text>
                    <View style={styles.ctaRow}>
                        <FLButton label={primary.label} variant="green" onPress={() => navigateTo(primary.to)} size="lg" style={styles.cta} />
                        <FLButton label="Compare plans" variant="paper" onPress={() => navigateTo('Plans')} size="lg" style={styles.cta} />
                    </View>
                </Reveal>
            </View>
        </FLPage>
    );
}

/** A route row: number, title, arrow; brightens on hover/focus. */
function JumpRow({ index, title, onPress, last }) {
    return (
        <View>
            <Pressable onPress={onPress} accessibilityRole="link" accessibilityLabel={title} style={styles.jumpRow} dataSet={{ flRow: '' }}>
                <Text style={[label(12), { width: 30, letterSpacing: 1.6 }]} dataSet={{ flRowNum: '' }}>{String(index).padStart(2, '0')}</Text>
                <Text style={styles.jumpTitle} dataSet={{ flRowBody: '' }}>{title}</Text>
                <View dataSet={{ flRowArrow: '' }}>
                    <Ionicons name="arrow-forward" size={20} color={FL.greenInk} />
                </View>
            </Pressable>
            {!last && <FLRule color={FL.hair} style={{ marginHorizontal: 16 }} />}
        </View>
    );
}

const WEB = Platform.OS === 'web';

const styles = StyleSheet.create({
    hero: { paddingTop: 48, alignSelf: 'center', width: '100%', maxWidth: 1480 },
    heroWide: { flexDirection: 'row', gap: 56, paddingTop: 72 },
    lede: { marginTop: 28, maxWidth: 640, color: FL.ink2 },
    ctaRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 12, marginTop: 32 },
    cta: { minWidth: 220 },

    jumps: { ...glass({ radius: 22 }), alignSelf: 'stretch', paddingVertical: 8, overflow: 'hidden' },
    jumpsHead: { paddingHorizontal: 20, paddingTop: 14, paddingBottom: 6, letterSpacing: 2 },
    jumpRow: { flexDirection: 'row', alignItems: 'center', gap: 12, paddingVertical: 18, paddingHorizontal: 20, minHeight: 56 },
    jumpTitle: { flex: 1, fontFamily: FONT, fontSize: 18, fontWeight: '700', color: FL.ink, letterSpacing: -0.2 },

    bandNote: { marginTop: 14, color: FL.muted, alignSelf: 'center', width: '100%', maxWidth: 1480 },

    section: { marginTop: 112, alignSelf: 'center', width: '100%', maxWidth: 1480 },
    steps: { flexDirection: 'row', marginTop: 40, gap: 16 },
    step: { ...glass({ radius: 22 }), flex: 1, paddingVertical: 28, paddingHorizontal: 24, gap: 10, minHeight: 240, overflow: 'hidden' },
    stepStacked: { minHeight: 0, paddingBottom: 32 },
    stepBar: { position: 'absolute', top: 0, left: 0, right: 0, height: 2, backgroundColor: FL.green, ...(WEB ? { backgroundImage: ACCENT_GRADIENT } : {}) },
    stepNum: { color: FL.greenInk, marginBottom: 14, letterSpacing: -1 },
    stepTitle: { fontFamily: FONT, fontSize: 20, fontWeight: '700', color: FL.ink, letterSpacing: -0.3 },

    statementBox: {
        ...glass({ strong: true, radius: 28 }),
        paddingVertical: 48,
        ...(WEB ? { backgroundImage: 'radial-gradient(ellipse 60% 120% at 0% 50%, rgba(124,58,237,0.20) 0%, transparent 70%)' } : {}),
    },
    statement: { fontFamily: FONT, fontWeight: '700', color: FL.ink, letterSpacing: -0.8, maxWidth: 1100 },
    highlight: { color: FL.greenInk },

    featRow: { flexDirection: 'row', alignItems: 'center', gap: 20, paddingVertical: 24, paddingHorizontal: 12, borderBottomWidth: 1, borderBottomColor: FL.hair },
    featRowStack: { alignItems: 'flex-start' },
    featNum: { ...label(12), color: FL.muted, width: 34, letterSpacing: 1.6 },
    featBody: { flex: 1, gap: 6 },
    featTitle: { fontFamily: FONT, fontSize: 20, fontWeight: '700', color: FL.ink, letterSpacing: -0.3 },

    closer: { paddingBottom: 96 },
    closerInner: {
        ...glass({ strong: true, radius: 28 }),
        alignSelf: 'center', width: '100%', maxWidth: 1480, paddingTop: 64, paddingBottom: 72, overflow: 'hidden',
        ...(WEB ? { backgroundImage: 'radial-gradient(ellipse 70% 90% at 100% 100%, rgba(37,99,235,0.22) 0%, transparent 65%), radial-gradient(ellipse 60% 80% at 0% 0%, rgba(124,58,237,0.24) 0%, transparent 65%)' } : {}),
    },
    closerBody: { color: FL.stageSoft, marginTop: 24, maxWidth: 560 },
});
